import { useEffect, useState } from 'react'
import { useAlert } from 'react-alert'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'

import type { ApiGetType, GetListApiResponse } from '../../api'
import { getApi } from '../../api'
import endpoints from '../../config/endpoints'
import pathnames from '../../config/pathnames'
import { useTableLoader } from '../../hooks/useTableLoader'
import type { Tenant } from '../../types/tenant'
import type { BreadcrumbItem } from '../../ui/Breadcrumb'
import { Breadcrumb } from '../../ui/Breadcrumb'
import { useLocationPagination } from '../../ui/Pagination'
import { StatusBadge } from '../../ui/StatusBadge'
import type { Column } from '../../ui/Table'
import { Table } from '../../ui/Table'
import { TableFilters } from '../../ui/TableFilters'
import { TitleHeader } from '../../ui/TitleHeader'
import { TypeBadge } from '../../ui/TypeBadge'

const Tenants = () => {
  const [search, setSearch] = useState('')
  const [redirectItem, setRedirectItem] = useState<Tenant | null>(null)
  const [pagination, setPagination] = useLocationPagination()

  const alert = useAlert()

  const { t } = useTranslation()

  const {
    items: tenants,
    loading,
    loadInitial,
    error,
    initial,
  } = useTableLoader<Tenant, Partial<ApiGetType>>({
    getApi: (api) => {
      const { ...params } = api.params ?? {}

      return getApi<GetListApiResponse<Tenant>>({
        ...api,
        endpoint: endpoints.tenants(),
        params: {
          ...params,
          ...(search && { search }),
        },
      })
    },
    setPagination,
  })

  useEffect(() => {
    loadInitial({}, pagination)
  }, [loadInitial, pagination, search])

  const breadcrumbItems: BreadcrumbItem[] = [
    { label: t('Breadcrumb.Home'), href: pathnames.dashboard },
    { label: t('Breadcrumb.Tenants'), href: pathnames.tenants },
  ]

  const columns: Column<Tenant>[] = [
    {
      header: 'Tenants.Fields.Name',
      render: (tenant) => tenant.name,
    },
    {
      header: 'Tenants.Fields.License',
      render: (tenant) => tenant.license?.package || '-',
    },
    {
      header: 'Tenants.Fields.Status',
      render: (tenant) => <StatusBadge status={tenant.status} />,
    },
    {
      header: 'Tenants.Fields.Type',
      render: (tenant) => <TypeBadge type={tenant.managementType} />,
    },
  ]

  if (error) {
    alert.error(error.message)
  }

  if (redirectItem) {
    return <Navigate to={pathnames.getTenantProfile(redirectItem.id)} />
  }

  return (
    <div className="p-6 bg-white text-sm">
      <Breadcrumb items={breadcrumbItems} />

      <TitleHeader
        title={t('Tenants.List.Title')}
        buttonText={t('Tenants.List.Create')}
        disabled={true}
      />

      <TableFilters search={search} setSearch={setSearch} />

      <Table
        data={tenants}
        initial={initial}
        isLoading={loading}
        columns={columns}
        emptyMessage="Tenants.List.Empty"
        pagination={pagination}
        setPagination={setPagination}
        onRowAction={(tenant) => setRedirectItem(tenant)}
      />
    </div>
  )
}

export default Tenants
